/**
 * QueVee's mark — wherever QueVee speaks or is asked (owner, 2026-11-02;
 * `docs/plans/macro-commentary.md`, drawings in `docs/mockups/quevee-icon.html`).
 *
 * **The brand's own drawing.** QuFolio (and the unbranded build) draws **G4**:
 * a gold coin Q with a four-point sparkle inside and the dollar's two strokes
 * on top as its ears. SweeterTrade — and TrendStrat, which wears SweeterTrade's
 * sign-in — draws **ST4**: the faceted strawberry from SweeterTrade's app icon
 * in four shades of green, its two gold leaves as the ears, the same sparkle.
 *
 * **Fixed colours, not theme tokens**: the mark is an identity, like a logo, so
 * it does not follow the accent. Each colour is a middle shade chosen to read
 * on both the dark and the light themes.
 *
 * **Motion** (CSS only): `playOnClick` swings the ears together, then
 * opposite, and twinkles the sparkle (~1.5s) on each click; `thinking` loops
 * the same, slower, while QueVee is writing. `prefers-reduced-motion` gets a
 * short blink instead.
 *
 * The visible word stays **AI** beside the mark (owner, 2026-11-02): few know the
 * name, so tooltips and the disclaimer carry "QueVee" — `QUEVEE_TOOLTIP`.
 */

import { useState, type CSSProperties, type ReactElement } from "react";

import { BRAND } from "../lib/brand-current";

/** What the mark's tooltip says wherever it stands for the AI. */
export const QUEVEE_TOOLTIP = "QueVee — the app's AI";

export type QueVeeMarkKind = "qf" | "st";

/** Which drawing a brand gets: SweeterTrade and its sandbox the strawberry, every other the coin. */
export function markFor(brandKey: string): QueVeeMarkKind {
  return brandKey === "sweetertrade" || brandKey === "trendstrat" ? "st" : "qf";
}

const GOLD = "#e0a82e";
const GREEN = "#27b463";

const CSS = `
.qv-mark .qv-l,.qv-mark .qv-r,.qv-mark .qv-spark{transform-box:fill-box}
.qv-mark .qv-spark{transform-origin:center}
.qv-mark[data-kind="qf"] .qv-l,.qv-mark[data-kind="qf"] .qv-r{transform-origin:center bottom}
.qv-mark[data-kind="st"] .qv-l{transform-origin:right bottom}
.qv-mark[data-kind="st"] .qv-r{transform-origin:left bottom}
.qv-play .qv-l{animation:qv-bothL 1.5s ease-in-out}
.qv-play .qv-r{animation:qv-bothR 1.5s ease-in-out}
.qv-play .qv-spark{animation:qv-twinkle .9s ease-out}
.qv-think .qv-l{animation:qv-bothL 2.2s ease-in-out infinite}
.qv-think .qv-r{animation:qv-bothR 2.2s ease-in-out infinite}
.qv-think .qv-spark{animation:qv-twinkle 2.2s ease-in-out infinite}
@keyframes qv-bothL{0%,100%{transform:rotate(0)}15%{transform:rotate(-26deg)}35%{transform:rotate(20deg)}50%{transform:rotate(0)}65%{transform:rotate(-26deg)}85%{transform:rotate(18deg)}}
@keyframes qv-bothR{0%,100%{transform:rotate(0)}15%{transform:rotate(-26deg)}35%{transform:rotate(20deg)}50%{transform:rotate(0)}65%{transform:rotate(26deg)}85%{transform:rotate(-18deg)}}
@keyframes qv-twinkle{0%{transform:scale(1) rotate(0)}30%{transform:scale(0) rotate(45deg)}70%{transform:scale(1.35) rotate(90deg)}100%{transform:scale(1) rotate(90deg)}}
@media (prefers-reduced-motion:reduce){
  .qv-play .qv-l,.qv-play .qv-r,.qv-play .qv-spark,.qv-think .qv-l,.qv-think .qv-r,.qv-think .qv-spark{animation:qv-blink .4s ease-in-out}
  @keyframes qv-blink{50%{opacity:.45}}
}`;

/** The keyframes, added to the page once whichever mark mounts first. */
let styled = false;
function ensureStyle(): void {
  if (styled || typeof document === "undefined") return;
  styled = true;
  const el = document.createElement("style");
  el.dataset.queveeMark = "";
  el.textContent = CSS;
  document.head.appendChild(el);
}

const SPARK = "M12 10.2 L12.95 13.05 L15.8 14 L12.95 14.95 L12 17.8 L11.05 14.95 L8.2 14 L11.05 13.05 Z";

function CoinQ(): ReactElement {
  return (
    <>
      <circle cx="12" cy="14" r="6.6" fill={GOLD} fillOpacity={0.18} />
      <circle cx="12" cy="14" r="6.6" fill="none" stroke={GOLD} strokeWidth="2.1" />
      <path d="M14.4 16.6 L17.6 19.8" stroke={GOLD} strokeWidth="2.1" strokeLinecap="round" />
      <path className="qv-spark" d={SPARK} fill={GOLD} />
      <path className="qv-l" d="M10.3 1.8 V8" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round" />
      <path className="qv-r" d="M13.7 1.8 V8" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round" />
    </>
  );
}

const T = "12 5.6", UL = "4.4 9.6", UR = "19.6 9.6", LL = "5.2 15.6", LR = "18.8 15.6", B = "12 22.6", C = "12 13";

function Strawberry(): ReactElement {
  return (
    <>
      <path d={`M${T} L${UR} L${C} Z`} fill={GREEN} fillOpacity={0.62} />
      <path d={`M${T} L${UL} L${C} Z`} fill={GREEN} fillOpacity={0.42} />
      <path d={`M${UL} L${LL} L${B} L${C} Z`} fill={GREEN} fillOpacity={0.3} />
      <path d={`M${UR} L${LR} L${B} L${C} Z`} fill={GREEN} fillOpacity={0.18} />
      <path d={`M${T} L${C} M${UL} L${C} L${UR} M${C} L${B}`} fill="none" stroke={GREEN} strokeWidth="0.7" strokeOpacity={0.75} />
      <path d={`M${T} L${UR} L${LR} L${B} L${LL} L${UL} Z`} fill="none" stroke={GREEN} strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M12 1.2 V6" stroke={GOLD} strokeWidth="1" strokeLinecap="round" />
      <path className="qv-l" d="M11.6 6 L6.8 2.2 L9 7.2 Z" fill={GOLD} stroke={GOLD} strokeWidth="0.6" strokeLinejoin="round" />
      <path className="qv-r" d="M12.4 6 L17.2 2.2 L15 7.2 Z" fill={GOLD} stroke={GOLD} strokeWidth="0.6" strokeLinejoin="round" />
      <path className="qv-spark" d="M12 10.6 L12.85 13.15 L15.4 14 L12.85 14.85 L12 17.4 L11.15 14.85 L8.6 14 L11.15 13.15 Z" fill={GOLD} />
    </>
  );
}

export interface QueVeeMarkProps {
  /** Width and height in CSS pixels. */
  size?: number;
  /** Loop the motion: QueVee is writing. */
  thinking?: boolean;
  /** Play the motion once on each click. The click still reaches the parent. */
  playOnClick?: boolean;
  /** A tooltip naming QueVee; without one the mark is decoration (`aria-hidden`). */
  title?: string;
  /** Tests only: the drawing, in place of the brand's. */
  kind?: QueVeeMarkKind;
  style?: CSSProperties;
}

export function QueVeeMark({ size = 16, thinking = false, playOnClick = false, title, kind, style }: QueVeeMarkProps) {
  ensureStyle();
  const drawing = kind ?? markFor(BRAND.key);
  // A new key remounts the svg, which is what restarts a CSS animation.
  const [plays, setPlays] = useState(0);
  const motion = thinking ? "qv-think" : plays > 0 ? "qv-play" : "";
  return (
    <span
      {...(title ? { role: "img", "aria-label": title, title } : { "aria-hidden": true })}
      data-quevee-mark={drawing}
      onClick={playOnClick ? () => setPlays((n) => n + 1) : undefined}
      style={{ display: "inline-flex", flex: "0 0 auto", lineHeight: 0, ...style }}
    >
      <svg
        key={plays}
        className={`qv-mark ${motion}`}
        data-kind={drawing}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        style={{ overflow: "visible", display: "block" }}
      >
        {drawing === "st" ? <Strawberry /> : <CoinQ />}
      </svg>
    </span>
  );
}
